import { useCallback, useEffect, useRef, useState } from 'react';

import useAuthProvider from 'store/authProvider/useAuthProvider';
import { API_AUTHORIZATION_URL } from 'config/api';
import {
  getValueFromToken,
  reauthenticateIfNeeded,
} from 'common/utils/providerHelpers/tokensHelper';

const INDENT_BEFORE_EXPIRATION = 10;
const RETRY_DELAY = 30000;

const useRefreshTokenSchedule = () => {
  const authProvider = useAuthProvider();
  const [actualExpTime, setActualTime] = useState(null);

  const timerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const refreshToken = useCallback(() => {
    if (isRefreshingRef.current) {
      return;
    }

    if (document.visibilityState === 'hidden') {
      return;
    }

    if (!navigator.onLine) {
      clearTimer();
      timerRef.current = setTimeout(refreshToken, RETRY_DELAY);
      return;
    }

    isRefreshingRef.current = true;

    reauthenticateIfNeeded({ apiAuthUrl: API_AUTHORIZATION_URL }, undefined, {})
      .then((tokenData) => {
        authProvider.checkAuth();

        const nextExp = getValueFromToken(tokenData?.accessToken)?.exp || null;
        setActualTime(nextExp);
      })
      .catch(() => {
        clearTimer();
        timerRef.current = setTimeout(refreshToken, RETRY_DELAY);
      })
      .finally(() => {
        isRefreshingRef.current = false;
      });
  }, [authProvider, clearTimer]);

  const scheduleRefresh = useCallback(() => {
    clearTimer();

    if (document.visibilityState === 'hidden') {
      return;
    }

    const exp = getValueFromToken()?.exp || actualExpTime;

    if (!exp) {
      timerRef.current = setTimeout(refreshToken, RETRY_DELAY);
      return;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    const delaySec = exp - nowSec - INDENT_BEFORE_EXPIRATION;

    if (delaySec <= 0) {
      refreshToken();
      return;
    }

    timerRef.current = setTimeout(refreshToken, delaySec * 1000);
  }, [actualExpTime, clearTimer, refreshToken]);

  useEffect(() => {
    scheduleRefresh();
  }, [scheduleRefresh]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearTimer();
        return;
      }

      scheduleRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', scheduleRefresh);

    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', scheduleRefresh);
    };
  }, [clearTimer, scheduleRefresh]);
};

export default useRefreshTokenSchedule;
